import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  DARK_QUERY,
  DRIVE_THEME_STORAGE_KEY,
  readStoredDriveTheme,
  storeDriveTheme,
  THEME_CHOSEN_KEY,
  THEME_STORAGE_KEY,
  applyThemeToDocument,
  detectPreselectedTheme,
  getSystemTheme,
  hasThemeChoice,
  isThemablePath,
  normalizeTheme,
  readStoredTheme,
  storeTheme,
  storeThemeChosen,
  themeAreaForPath,
} from "../lib/theme";
import { isDrivePath } from "../lib/drive/head";

const ThemeContext = createContext({
  theme: "light", // preferenza: light | dark | system
  effectiveTheme: "light",
  themable: false,
  needsThemeChoice: false,
  preselectedTheme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
  previewTheme: () => {},
  confirmThemeChoice: () => {},
});

/**
 * Preferenza utente (light | dark | system, default light) persistita in localStorage.
 * Il tema EFFETTIVO è sempre "light" sulle pagine pubbliche (vedi LIGHT_ONLY_PREFIXES).
 * `pathname` arriva dal router (vedi RoutedThemeProvider in App.js): il provider non dipende da react-router.
 *
 * Primo accesso: finché non esiste né `affidya-theme` né `affidya-theme-chosen`, `needsThemeChoice` è true
 * e <ThemeChoiceDialog/> (montato dai layout autenticati) chiede la scelta. Durante il dialog il tema scelto
 * si applica subito come ANTEPRIMA (`previewTheme`) senza salvare nulla; `confirmThemeChoice` salva.
 */
export function ThemeProvider({ children, pathname = "/" }) {
  const [appTheme, setThemeState] = useState(readStoredTheme);
  const [driveTheme, setDriveTheme] = useState(readStoredDriveTheme);
  const inDrive = isDrivePath(pathname);
  // In Drive vale la preferenza Drive (default "system"), altrove quella Affidya (default "light")
  const theme = inDrive ? driveTheme : appTheme;
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const [preview, setPreview] = useState(null);
  const [needsChoice, setNeedsChoice] = useState(() => !hasThemeChoice());
  const themable = isThemablePath(pathname);
  const area = themeAreaForPath(pathname);
  const resolved = preview || (theme === "system" ? systemTheme || "light" : theme === "dark" ? "dark" : "light");
  const effectiveTheme = themable ? resolved : "light";

  useEffect(() => {
    applyThemeToDocument(effectiveTheme, area);
  }, [effectiveTheme, area]);

  // "Sistema": segue il cambio di tema dell'OS in tempo reale
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(DARK_QUERY);
    if (!mq) return undefined;
    const onChange = (e) => setSystemTheme(e && typeof e.matches === "boolean" ? (e.matches ? "dark" : "light") : getSystemTheme());
    if (typeof mq.addEventListener === "function") mq.addEventListener("change", onChange);
    else if (typeof mq.addListener === "function") mq.addListener(onChange);
    return () => {
      if (typeof mq.removeEventListener === "function") mq.removeEventListener("change", onChange);
      else if (typeof mq.removeListener === "function") mq.removeListener(onChange);
    };
  }, []);

  // Allineamento fra schede/finestre aperte
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === null || e.key === DRIVE_THEME_STORAGE_KEY) setDriveTheme(readStoredDriveTheme());
      if (e.key === null || e.key === THEME_STORAGE_KEY || e.key === THEME_CHOSEN_KEY) {
        setThemeState(readStoredTheme());
        setNeedsChoice(!hasThemeChoice());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setTheme = useCallback((next) => {
    const v = normalizeTheme(next);
    if (inDrive) {
      setDriveTheme(v);
      storeDriveTheme(v);
      setPreview(null);
      return;
    }
    setThemeState(v);
    storeTheme(v);
    storeThemeChosen();
    setNeedsChoice(false);
    setPreview(null);
  }, [inDrive]);

  // Interruttore sole/luna: passa al tema opposto a quello effettivo (scelta esplicita)
  const toggleTheme = useCallback(() => {
    setTheme(resolved === "dark" ? "light" : "dark");
  }, [resolved, setTheme]);

  const previewTheme = useCallback(
    (next) => setPreview(next ? (next === "system" ? getSystemTheme() || "light" : normalizeTheme(next)) : null),
    []
  );

  const confirmThemeChoice = useCallback((choice) => setTheme(choice), [setTheme]);

  const value = useMemo(
    () => ({
      theme,
      effectiveTheme,
      themable,
      needsThemeChoice: inDrive ? false : needsChoice,
      preselectedTheme: detectPreselectedTheme(),
      setTheme,
      toggleTheme,
      previewTheme,
      confirmThemeChoice,
    }),
    [theme, effectiveTheme, themable, needsChoice, inDrive, setTheme, toggleTheme, previewTheme, confirmThemeChoice]
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useAppTheme() {
  return useContext(ThemeContext);
}
